import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { notifications } from '@mantine/notifications';

import {
    ABORT_TRAINING_MATERIAL_UPLOAD,
    CREATE_TRAINING_MATERIAL,
    GET_TRAINING_MATERIAL_THUMBNAIL_UPLOAD_URL,
    GET_TRAINING_MATERIAL_UPLOAD_URL,
} from '../apollo/mutations/training';
import { GET_TRAINING_MATERIALS } from '../apollo/queries/training';
import { captureTrainingThumbnail } from '../helpers/captureTrainingThumbnail';
import { getErrorMessage } from '../helpers/getErrorMessage';
import { uploadFile } from '../services/dataImport.service';
import { resolveTrainingFileType } from '../types/training';

type UploadArgs = {
    file: File;
    title: string;
    description?: string;
    categoryId: number;
    fileType?: string;
};

export function useTrainingUpload() {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [getUploadUrl] = useMutation(GET_TRAINING_MATERIAL_UPLOAD_URL);
    const [getThumbnailUploadUrl] = useMutation(
        GET_TRAINING_MATERIAL_THUMBNAIL_UPLOAD_URL,
    );
    const [createMaterial] = useMutation(CREATE_TRAINING_MATERIAL, {
        refetchQueries: [{ query: GET_TRAINING_MATERIALS }],
    });
    const [abortUpload] = useMutation(ABORT_TRAINING_MATERIAL_UPLOAD);

    const upload = useCallback(
        async ({
            file,
            title,
            description,
            categoryId,
            fileType,
        }: UploadArgs) => {
            setUploading(true);
            setUploadProgress(5);
            let uploadedS3Key: string | undefined;
            let thumbnailS3Key: string | undefined;
            try {
                const resolvedType =
                    fileType ||
                    resolveTrainingFileType(file) ||
                    file.type ||
                    'application/octet-stream';

                const { data } = await getUploadUrl({
                    variables: {
                        fileName: file.name,
                        fileType: resolvedType,
                        categoryId,
                        fileSize: file.size,
                    },
                });

                const uploadResult = data?.getTrainingMaterialUploadUrl;
                if (!uploadResult?.url || !uploadResult?.s3Key) {
                    throw new Error('Failed to get upload URL');
                }
                uploadedS3Key = uploadResult.s3Key;

                setUploadProgress(10);

                const thumbnailBlob = await captureTrainingThumbnail(file);

                await uploadFile(
                    file,
                    uploadResult.url,
                    resolvedType,
                    percent => {
                        setUploadProgress(10 + Math.round(percent * 0.75));
                    },
                );

                setUploadProgress(88);

                if (thumbnailBlob) {
                    try {
                        const { data: thumbnailData } =
                            await getThumbnailUploadUrl({
                                variables: {
                                    fileS3Key: uploadResult.s3Key,
                                    categoryId,
                                },
                            });
                        const thumbnailUpload =
                            thumbnailData?.getTrainingMaterialThumbnailUploadUrl;
                        if (thumbnailUpload?.url && thumbnailUpload?.s3Key) {
                            const allocatedThumbnailStorageKey =
                                thumbnailUpload.s3Key;
                            try {
                                await uploadFile(
                                    thumbnailBlob,
                                    thumbnailUpload.url,
                                    'image/jpeg',
                                );
                                thumbnailS3Key = allocatedThumbnailStorageKey;
                            } catch (error) {
                                await abortUpload({
                                    variables: {
                                        input: {
                                            s3Keys: [
                                                allocatedThumbnailStorageKey,
                                            ],
                                        },
                                    },
                                });
                                throw error;
                            }
                        }
                    } catch (thumbnailError) {
                        thumbnailS3Key = undefined;
                        notifications.show({
                            title: 'Thumbnail skipped',
                            message: getErrorMessage(thumbnailError),
                            color: 'yellow',
                        });
                    }
                }

                setUploadProgress(94);

                await createMaterial({
                    variables: {
                        input: {
                            title,
                            description: description || null,
                            s3Key: uploadResult.s3Key,
                            thumbnailS3Key,
                            fileName: file.name,
                            fileSize: file.size,
                            fileType: resolvedType,
                            categoryId,
                        },
                    },
                });

                setUploadProgress(100);

                notifications.show({
                    title: 'Upload complete',
                    message: `"${title}" is now available in Training`,
                    color: 'teal',
                });
            } catch (error) {
                const keysToAbort: string[] = [];
                if (uploadedS3Key) {
                    keysToAbort.push(uploadedS3Key);
                }
                if (thumbnailS3Key) {
                    keysToAbort.push(thumbnailS3Key);
                }
                if (keysToAbort.length > 0) {
                    try {
                        await abortUpload({
                            variables: { input: { s3Keys: keysToAbort } },
                        });
                    } catch (cleanupError) {
                        notifications.show({
                            title: 'Upload cleanup failed',
                            message: getErrorMessage(cleanupError),
                            color: 'orange',
                        });
                    }
                }
                notifications.show({
                    title: 'Upload failed',
                    message: getErrorMessage(error),
                    color: 'red',
                });
                throw error;
            } finally {
                setUploading(false);
                setUploadProgress(0);
            }
        },
        [abortUpload, createMaterial, getThumbnailUploadUrl, getUploadUrl],
    );

    return { upload, uploading, uploadProgress };
}
